import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { FileText, ExternalLink, CheckCircle2 } from 'lucide-react';
import { onsiteApiFetch } from '../config/api.js';
import type { OnsiteJob } from '../../../api/src/types/onsite-types.js';

export default function ReportPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [reportUrl, setReportUrl] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['job', id],
    queryFn: () => onsiteApiFetch<{ job: OnsiteJob }>(`/api/v1/onsite/jobs/${id}`),
    enabled: !!id,
  });

  const generateReport = useMutation({
    mutationFn: () =>
      onsiteApiFetch<{ report_url: string; job_id: string; generated_at: string }>(
        `/api/v1/onsite/jobs/${id}/report`,
        { method: 'POST' }
      ),
    onSuccess: data => setReportUrl(data.report_url),
  });

  const job = data?.job;

  return (
    <div className="flex h-full flex-col items-center justify-center gap-6 p-6">
      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-secondary">
        <FileText className="h-10 w-10 text-primary" />
      </div>

      <div className="text-center">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Service Report</h1>
        {job && (
          <p className="mt-1 text-muted-foreground">
            {job.title} — {job.customer_name}
          </p>
        )}
      </div>

      {reportUrl ? (
        <div className="flex w-full max-w-sm flex-col gap-3">
          <div className="flex items-center gap-3 rounded-xl bg-green-500/10 px-4 py-3 text-green-400">
            <CheckCircle2 className="h-5 w-5 shrink-0" />
            <span className="text-sm font-medium">Report generated successfully</span>
          </div>
          <a
            href={reportUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 font-medium text-foreground hover:bg-primary/90"
          >
            <ExternalLink className="h-4 w-4" />
            Open PDF
          </a>
          <button
            onClick={() => navigate(`/jobs/${id}`)}
            className="rounded-xl border border-border px-4 py-3 text-sm text-muted-foreground hover:bg-secondary"
          >
            Back to Job
          </button>
        </div>
      ) : (
        <div className="flex w-full max-w-sm flex-col gap-3">
          <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
            <p className="mb-2 font-medium text-foreground">Report includes:</p>
            <ul className="space-y-1 list-disc pl-4">
              <li>Job details and customer info</li>
              <li>Completed checklist items</li>
              <li>Time on site and travel time</li>
              <li>Parts used and costs</li>
              <li>Customer signature</li>
              <li>Site timeline (check-in/out)</li>
            </ul>
          </div>
          <button
            onClick={() => generateReport.mutate()}
            disabled={generateReport.isPending}
            className="flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-4 text-lg font-semibold text-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {generateReport.isPending ? (
              <>
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Generating…
              </>
            ) : (
              <>
                <FileText className="h-5 w-5" />
                Generate Report
              </>
            )}
          </button>
          <button
            onClick={() => navigate(`/jobs/${id}`)}
            className="rounded-xl border border-border px-4 py-3 text-sm text-muted-foreground hover:bg-secondary"
          >
            Back to Job
          </button>
        </div>
      )}
    </div>
  );
}
